import { useEffect, useMemo } from "react";

import { proxy, useSnapshot } from "valtio";

import en from "./en";
import mitt from "../utils/mitt";
import { DEFAULT_LANG_VALUE } from "../constants";

export const DEFAULT_LOCALE = {
	lang: DEFAULT_LANG_VALUE,
	message: {
		en
	}
};

class Locale {
	emitter;
	constructor() {
		this.emitter = mitt();
	}

	get lang() {
		return DEFAULT_LOCALE.lang;
	}

	set lang(lang) {
		if (!this.isLangSupported(lang)) {
			console.warn(
				`Can't find the current language "${lang}", Using language "${DEFAULT_LOCALE.lang}" by default`
			);
			return;
		}

		DEFAULT_LOCALE.lang = lang;
		this.emitter.emit("lang", lang);
	}

	get message() {
		return DEFAULT_LOCALE.message;
	}

	set message(message) {
		DEFAULT_LOCALE.message = message;
	}

	loadLangMessage(lang) {
		return this.message[lang];
	}

	isLangSupported(lang) {
		const supportedLangs = Object.keys(this.message);
		return supportedLangs.includes(lang);
	}
	setLang(lang) {
		this.lang = lang;
	}

	registerWatchLang(hook) {
		this.emitter.on("lang", hook);

		const unsubscribe = () => {
			this.emitter.off("lang", hook);
		};

		return {
			unsubscribe
		};
	}

	setMessage(lang, message) {
		this.message[lang] = message;
	}

	buildLocalesHandler(lang) {
		if (!lang) {
			lang = this.lang;
		}

		const message = this.loadLangMessage(lang);

		return function t(path) {
			return message[path] || path;
		};
	}
}

const locale = new Locale();

const atomLang = proxy({
	lang: DEFAULT_LOCALE.lang
});

function useLocale() {
	const atomLangSnap = useSnapshot(atomLang);

	const t = useMemo(() => {
		return locale.buildLocalesHandler(atomLangSnap.lang);
	}, [atomLangSnap.lang]);

	useEffect(() => {
		const watchLang = locale.registerWatchLang(val => {
			atomLang.lang = val;
		});

		return () => {
			watchLang.unsubscribe();
		};
	}, []);

	return {
		lang: atomLangSnap.lang,
		t
	};
}

const localeActions = {
	t: path => {
		return locale.buildLocalesHandler(atomLang.lang)(path);
	}
};

export default locale;
export { Locale, localeActions, useLocale };

export { default as en } from "./en";
